import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import apiClient from '../services/api';
import { authService } from '../services/auth';
import {
  clearSilentSsoFailedFlag,
  hasSilentSsoFailedThisVisit,
  markSilentSsoFailedThisVisit,
  rememberSsoReturnTo,
} from '../utils/silentSso';

interface RequireSessionProps {
  children: (user: any, logout: () => void) => React.ReactNode;
}

/**
 * Route protégée : sans session locale, redirection pleine page vers Keycloak
 * avec prompt=none. Un échec de cette visite affiche la page de login.
 */
const RequireSession: React.FC<RequireSessionProps> = ({ children }) => {
  const location = useLocation();
  const startedRef = useRef(false);
  const [user, setUser] = useState<any>(null);
  const [phase, setPhase] = useState<'checking' | 'silent' | 'ready' | 'login'>('checking');

  useEffect(() => {
    let cancelled = false;

    const loadLocal = async () => {
      if (authService.isAuthenticated()) {
        try {
          const profile = await authService.getProfile();
          if (!cancelled) {
            setUser(profile);
            setPhase('ready');
          }
          return;
        } catch {
          authService.logout();
        }
      }

      try {
        const response = await apiClient.get('/auth/session');
        if (!cancelled) {
          setUser(response.data);
          setPhase('ready');
        }
      } catch {
        if (!cancelled) {
          setUser(null);
          setPhase(hasSilentSsoFailedThisVisit() ? 'login' : 'silent');
        }
      }
    };

    void loadLocal();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (phase !== 'silent') return;
    if (hasSilentSsoFailedThisVisit()) {
      setPhase('login');
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;

    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    rememberSsoReturnTo(returnTo);

    void (async () => {
      try {
        const response = await apiClient.get('/auth/sso/start', {
          params: { prompt: 'none', returnTo },
        });
        const authUrl = response.data?.authUrl as string | undefined;
        if (response.data?.provider === 'keycloak' && authUrl) {
          const url = new URL(authUrl);
          if (url.searchParams.get('prompt') !== 'none') {
            throw new Error('prompt_inattendu');
          }
          window.location.assign(authUrl);
          return;
        }
        markSilentSsoFailedThisVisit();
        setPhase('login');
      } catch {
        markSilentSsoFailedThisVisit();
        setPhase('login');
      }
    })();
  }, [phase, location.pathname, location.search, location.hash]);

  const logout = () => {
    void (async () => {
      let logoutUrl: string | undefined;
      try {
        const result = await apiClient.post('/auth/sso/logout');
        logoutUrl = result.data?.logoutUrl || undefined;
      } catch {
        /* session déjà absente */
      }
      authService.logout();
      clearSilentSsoFailedFlag();
      setUser(null);
      if (logoutUrl) {
        window.location.assign(logoutUrl);
        return;
      }
      window.location.assign('/login');
    })();
  };

  if (phase === 'ready' && user) {
    return <>{children(user, logout)}</>;
  }

  if (phase === 'login') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-3" />
        <p className="text-sm text-slate-600">
          {phase === 'silent' ? 'Connexion sécurisée…' : 'Vérification de session…'}
        </p>
      </div>
    </div>
  );
};

export default RequireSession;
